import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiLock } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteRecruitment, getAdminRecruitments as getRecruitments } from 'apis/adminRecruitments';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const RecruitmentsList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const { data: languages = [] } = useActiveLanguagesQuery();
    const queryParams = useMemo(() => ({
        pageNumber: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter]);
    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('recruitments', queryParams, getRecruitments);
    const deleteMutation = useAdminDeleteMutation('recruitments', deleteRecruitment);

    useEffect(() => { setCurrentPage(1); }, [keyword, isActiveFilter, languageCodeFilter]);
    useEffect(() => { if (error) toast.error(error.message || 'T?i d? li?u th?t b?i'); }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res?.success) {
                toast.success('Xóa thành công');
            } else {
                toast.error(res?.message || 'Xóa thất bại');
            }
        } catch {
            toast.error('Xóa thất bại');
        }
        setDeleteId(null);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('vi-VN');
    };

    const columns = [
        { field: 'id', header: 'ID' },
        {
            header: 'Tiêu đề',
            render: (row) => row.title || '—',
        },
        {
            header: 'Phòng ban',
            render: (row) => row.department || row.departmentCode || '—',
        },
        { field: 'slug', header: 'Slug' },
        {
            header: 'Ngày đăng',
            render: (row) => formatDate(row.publishedAt),
        },
        {
            header: 'Hết hạn',
            render: (row) => formatDate(row.expiresAt),
        },
        {
            header: 'Trạng thái',
            render: (row) => {
                const expiresAt = row.expiresAt || row.ExpiresAt;
                const isExpired = expiresAt && new Date(expiresAt).getTime() < Date.now();
                if (isExpired) {
                    return (
                        <span className="status-badge expired">
                            Quá hạn
                        </span>
                    );
                }
                return (
                    <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>
                        {row.isActive ? 'Hoạt động' : 'Không hoạt động'}
                    </span>
                );
            },
        },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/recruitments/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button>
                    <button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Khóa / Ẩn"><FiLock size={15} /></button>
                </div>
            ),
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Tuyển dụng"
                subtitle="Quản lý tin đăng tuyển dụng"
                action={<Button onClick={() => navigate('/admin/recruitments/create')}>+ Thêm Tin tuyển dụng</Button>}
            />
            <div className="filters-bar">
                <div className="filter-item">
                    <Input
                        placeholder="Tìm theo tiêu đề..."
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                    />
                </div>
                <div className="filter-item">
                    <Select
                        value={isActiveFilter}
                        onChange={e => setIsActiveFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả trạng thái' },
                            { value: 'true', label: 'Hoạt động' },
                            { value: 'false', label: 'Không hoạt động' },
                        ]}
                    />
                </div>
                <div className="filter-item">
                    <Select
                        value={languageCodeFilter}
                        onChange={e => setLanguageCodeFilter(e.target.value)}
                        options={[
                            ...languages.map(l => ({ value: l.code, label: l.name })),
                        ]}
                    />
                </div>
            </div>
            <Table
                columns={columns}
                data={data.items}
                loading={isFetching}
                pagination={{
                    currentPage: data.currentPage,
                    totalPages: Math.ceil(data.total / data.pageSize) || 1,
                    onPageChange: setCurrentPage,
                }}
            />
            <ConfirmationDialog
                open={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Khóa / Ẩn Tin tuyển dụng"
                message="Bạn có chắc chắn muốn khóa/ẩn tin tuyển dụng này không?"
            />
        </div>
    );
};

export default RecruitmentsList;
